<template>
  <b-form v-if="currentScreen === 'New'" @submit.prevent="nextForm()">
    <b-form-group
      id="input-group-title"
      label="First, name your file."
      label-for="input-title"
      class="mb-4 font-weight-bold"
      v-slot="{ ariaDescribedby }"
    >
      <b-form-input
        id="input-title"
        type="text"
        v-model="file.name"
        required
        :placeholder="namePlaceholder"
        :aria-describedby="ariaDescribedby"
      ></b-form-input>

      <b-form-invalid-feedback id="input-title-feedback">
        <!-- eslint-disable-next-line -->
      </b-form-invalid-feedback>

      <div v-if="checkingName" class="mt-2 text-secondary">
        <small>Checking for existing files…</small>
      </div>
      <div v-else-if="nameMatches.length" class="mt-3">
        <p class="mb-2 font-weight-normal text-secondary">
          {{ existingFilesSubject }} {{ nameMatches.length }} file<template
            v-if="nameMatches.length > 1"
            >s</template
          >
          with a similar name. You can edit an existing one instead of creating
          a new file:
        </p>
        <existing-file-list
          :files="nameMatches"
          :show-company-approval="isBusinessAdminUpload"
          @edit="onEditExistingFile"
          @view="onViewExistingFile"
        />
      </div>
    </b-form-group>

    <b-form-group
      id="input-group-title"
      label="Second, select the type of information you would like to add to your file."
      label-for="input-title"
      class="mb-4 font-weight-bold"
      v-slot="{ ariaDescribedby }"
    >
      <b-form-invalid-feedback
        class="mb-2 font-weight-normal"
        :state="infoTypeState"
        >The information types must have at least 1
        item.</b-form-invalid-feedback
      >
      <b-form-checkbox-group
        class="font-weight-normal type-group"
        id="checkbox-types"
        :aria-describedby="ariaDescribedby"
        v-model="selectedInfoTypes"
        name="types"
        stacked
        ><b-form-checkbox
          v-for="type in fileInfoTypes"
          :key="type.id"
          :value="type"
          class="mb-2"
        >
          {{ type.label }}
        </b-form-checkbox>
      </b-form-checkbox-group>

      <b-form-invalid-feedback id="input-title-feedback">
        <!-- eslint-disable-next-line -->
      </b-form-invalid-feedback>
    </b-form-group>

    <!-- <b-form-group
      id="input-group-title"
      label="Finally, choose how many files you would like to upload."
      label-for="input-title"
      class="mb-4 font-weight-bold"
      v-slot="{ ariaDescribedby }"
    >
      <b-form-radio-group
        id="radio-group"
        v-model="file.uploadType"
        required
        :aria-describedby="ariaDescribedby"
        name="upload-type"
        stacked
      >
        <b-form-radio
          name="single"
          value="single"
          class="font-weight-normal mb-2"
          >Single file (perfect for word documents)</b-form-radio
        >
        <b-form-radio
          name="multiple"
          value="multiple"
          class="font-weight-normal"
          >Multiple files / back to front (suitable for licences)</b-form-radio
        >
      </b-form-radio-group>

      <b-form-invalid-feedback id="input-title-feedback">
        eslint-disable-next-line
      </b-form-invalid-feedback>
    </b-form-group> -->

    <b-row class="mt-5">
      <b-col>
        <b-button
          block
          class="btn-outlined bg-white text-dark"
          variant="outline-secondary"
          v-if="isRequestFileCreation"
          :to="{
            name: 'request.request-file-templates-page',
            params: { fileRequestUserId: fileRequestUserId },
          }"
          >Back</b-button
        >
        <b-button
          v-else
          block
          class="btn-outlined bg-white text-dark"
          variant="outline-secondary"
          :to="{ name: 'templates.list-page' }"
          >Back</b-button
        >
      </b-col>
      <b-col>
        <b-button block type="submit" variant="primary"> Next </b-button>
      </b-col>
    </b-row>
  </b-form>
  <file-form
    v-else
    :file="file"
    :selectedFields="selectedInfoTypes"
    :isRequestFileCreation="isRequestFileCreation"
    :request="request"
    @back="currentScreen = 'New'"
  />
</template>

<script>
import { mapState, mapActions, mapMutations } from 'vuex'
import Form from '@/utils/form'
import FileForm from '@/components/forms/FileForm'
import ExistingFileList from '@/components/lists/ExistingFileList'
import fileDuplicateMatch from '@/mixins/fileDuplicateMatch'
import { ELECTRICAL_FIELD_TYPES } from '@/utils/fileLabels'

export default {
  name: 'NewFileForm',

  mixins: [fileDuplicateMatch],

  components: {
    FileForm,
    ExistingFileList,
  },

  props: {
    isRequestFileCreation: {
      type: Boolean,
      default: false,
    },
    fileRequestUserId: {
      type: [Number, String],
      default: null,
    },
    request: {
      type: Object,
      default: null,
    },
  },

  data() {
    return {
      file: new Form({
        name: '',
        uploadType: '',
      }),
      selectedInfoTypes: [],
      currentScreen: 'New',
      infoTypeState: null,
      // Live "file with this name already exists" check for the no-template
      // ("New Upload") flow. Debounced as the user types the name.
      nameMatches: [],
      checkingName: false,
      nameCheckTimer: null,
      nameCheckToken: 0,
      windowWidth: typeof window !== 'undefined' ? window.innerWidth : 1024,
    }
  },

  computed: {
    ...mapState({
      infoTypes: (state) => state.file.infoTypes,
    }),
    fileInfoTypes() {
      return this.infoTypes.filter(
        (type) =>
          type.label.toLowerCase() !== 'consent' &&
          !ELECTRICAL_FIELD_TYPES.includes(type.field_type),
      )
    },

    // Long example placeholders get clipped on narrow inputs, so show a concise
    // hint on phones (still readable at full size) and the full one on wider screens.
    namePlaceholder() {
      return this.windowWidth < 576
        ? 'e.g. WA Driver licence'
        : 'WA - Driver licence, SA - White card etc.'
    },
  },

  methods: {
    ...mapActions({
      getInfoTypes: 'file/getInfoTypes',
      fileRequestShare: 'requests/fileRequestShare',
    }),
    ...mapMutations({
      setCurrentScreen: 'file/setCurrentScreen',
    }),
    nextForm() {
      this.infoTypeState = this.selectedInfoTypes.length >= 1
      if (this.infoTypeState) {
        this.currentScreen = 'Upload'
      }
    },

    onEditExistingFile(file) {
      if (!file) return
      const id = file.file_id ? file.file_id : file.id
      this.$router.push({
        name: 'file.edit-page',
        params: { id },
      })
    },

    onViewExistingFile(file) {
      if (!file) return
      const id = file.file_id ? file.file_id : file.id
      this.$router.push({
        name: 'file.view-page',
        params: { id },
      })
    },

    /**
     * Look up files of the same name the current actor can see and surface them
     * as a heads-up. Guarded by a token so only the latest lookup wins.
     */
    async runNameCheck(name) {
      const token = (this.nameCheckToken += 1)
      try {
        const matches = await this.findFilesMatchingName(name)
        if (token === this.nameCheckToken) this.nameMatches = matches
      } catch (err) {
        if (token === this.nameCheckToken) this.nameMatches = []
      } finally {
        if (token === this.nameCheckToken) this.checkingName = false
      }
    },

    onWindowResize() {
      this.windowWidth = window.innerWidth
    },
  },

  watch: {
    currentScreen(value) {
      this.setCurrentScreen(value)
    },

    request(newVal, oldVal) {
      if (this.isRequestFileCreation && newVal) {
        this.file.name = newVal.name
        this.selectedInfoTypes = newVal.information_types

        this.nextForm()
      }
    },

    selectedInfoTypes(value) {
      this.infoTypeState = value.length >= 1
    },

    'file.name'(value) {
      clearTimeout(this.nameCheckTimer)
      // The request-file flow uses a fixed, request-provided name; no check.
      const name = (value || '').trim()
      if (this.isRequestFileCreation || name.length < 2) {
        this.nameMatches = []
        this.checkingName = false
        return
      }
      this.checkingName = true
      this.nameCheckTimer = setTimeout(() => this.runNameCheck(name), 400)
    },
  },

  created() {
    this.getInfoTypes()
  },

  mounted() {
    window.addEventListener('resize', this.onWindowResize)
  },

  beforeDestroy() {
    clearTimeout(this.nameCheckTimer)
    window.removeEventListener('resize', this.onWindowResize)
  },
}
</script>

<style lang="scss" scoped>
.type-group {
  /* Single column on phones so each checkbox label has room; two columns once
   * there's width to spare. */
  column-count: 1;

  @media (min-width: 576px) {
    column-count: 2;
  }

  @media (min-width: 767px) {
    padding-right: 10em;
  }
}
</style>
